/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : UpdateTestCaseDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Checkbox,
  DialogContentText,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import { componentStyle } from "../../../config/appTheme";
import CustomMultiSelectAutocompleteWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomMultiSelectAutocompleteWithApiV2";

const COMPONENT = { EE: "ee", SWM: "swm", VS: "vs", VC: "vc" };
const EMPTY_PRE_CONDITION = { condition_number: "", description: "" };
const EMPTY_STEPS = {
  step_number: "",
  expected_result: "",
  description: "",
};

const toBoolean = (value) => value === true || value === "true" || value === 1;

const getEntityLabel = (entity, entityType) => {
  if (!entity) return "";

  if (entityType === COMPONENT.EE) return entity?.description || "";
  if (entityType === COMPONENT.SWM) {
    return entity?.description || entity?.name || "";
  }
  if (entityType === COMPONENT.VS) {
    return entity?.description || entity?.assembly_number || entity?.name || "";
  }
  if (entityType === COMPONENT.VC) {
    return (
      entity?.description || entity?.component_number || entity?.name || ""
    );
  }

  return entity?.description || entity?.name || "";
};

const mapEntityToOption = (entity, entityType) => ({
  value:
    entity?.ee_id ??
    entity?.swm_id ??
    entity?.vs_id ??
    entity?.vc_id ??
    entity?.id,
  label: getEntityLabel(entity, entityType),
});

const mapEntityIdsToOptions = (entityIds = [], options = []) =>
  entityIds?.map((id) => {
    const found = options?.find((item) => String(item?.value) === String(id));
    return found || { value: id, label: String(id) };
  }) || [];

const getUniqueLegalRequirementRequests = (requirements = []) => {
  const uniqueRequests = new Map();
  for (const req of requirements) {
    const lsId = req?.ls_id;
    const chapter = req?.chapter;
    if (!lsId || !chapter) continue;
    const key = `${lsId}-${chapter}`;
    if (!uniqueRequests.has(key)) {
      uniqueRequests.set(key, { lsId, chapter });
    }
  }
  return Array.from(uniqueRequests.values());
};

const extractLegalRequirementRows = (resp) =>
  resp?.data?.data ??
  resp?.data ??
  resp?.response?.data?.data ??
  resp?.response?.data ??
  [];

const buildRequirementLabelMap = (responses = []) => {
  const labelMap = new Map();
  for (const resp of responses) {
    const rows = extractLegalRequirementRows(resp);
    for (const lr of rows || []) {
      if (lr?.lr_id === null || lr?.lr_id === undefined) continue;
      const label = lr?.req_number || lr?.description || String(lr?.lr_id);
      labelMap.set(String(lr?.lr_id), label);
    }
  }
  return labelMap;
};

function UpdateTestCaseDialog({
  open,
  onClose,
  selectedTestCaseData,
  setSelectedTestCaseData,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const testCaseStatus = useSelector(
    (state) => state.settingsReducer?.metaData?.test_case_status,
  );
  const [mainTestCaseId, setMainTestCaseId] = useState(null);
  const [testCaseId, setTestCaseId] = useState("");
  const [testCaseIdError, setTestCaseIdError] = useState("");
  const [testCaseName, setTestCaseName] = useState("");
  const [testCaseNameError, setTestCaseNameError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [environmentOptionsList, setEnvironmentOptionsList] = useState([]);
  const [environment, setEnvironment] = useState(null);
  const [isDestructible, setIsDestructible] = useState(false);
  const [needNewVehicle, setNeedNewVehicle] = useState(false);
  const [eeComponentsOptionsList, setEEComponentsOptionsList] = useState([]);
  const [selectedEEComponentIds, setSelectedEEComponentIds] = useState([]);
  const [softwareModulesOptionsList, setSoftwareModulesOptionsList] = useState(
    [],
  );
  const [selectedSoftwareModuleIds, setSelectedSoftwareModuleIds] = useState(
    [],
  );
  const [vehicleSystemsOptionsList, setVehicleSystemsOptionsList] = useState(
    [],
  );
  const [selectedVehicleSystemIds, setSelectedVehicleSystemIds] = useState([]);
  const [vehicleComponentsOptionsList, setVehicleComponentsOptionsList] =
    useState([]);
  const [selectedVehicleComponentIds, setSelectedVehicleComponentIds] =
    useState([]);
  const [selectedComponent, setSelectedComponent] = useState(COMPONENT.EE);
  const [originalEEComponentIds, setOriginalEEComponentIds] = useState([]);
  const [originalRequirementIds, setOriginalRequirementIds] = useState([]);

  const [type, setType] = useState(null);
  const [statusOptionsList, setStatusOptionsList] = useState([]);
  const [status, setStatus] = useState(null);
  const [preConditions, setPreConditions] = useState([
    { ...EMPTY_PRE_CONDITION },
  ]);
  const [testCaseSteps, setTestCaseSteps] = useState([{ ...EMPTY_STEPS }]);
  const [expectedResult, setExpectedResult] = useState("");
  const [legalStandardOptionsList, setLegalStandardOptionsList] = useState([]);
  const [legalStandardsList, setLegalStandardsList] = useState([]);
  const [selectedLegalStandardId, setSelectedLegalStandardId] = useState(null);
  const [chapterOptionsList, setChapterOptionsList] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [requirementOptionsList, setRequirementOptionsList] = useState([]);
  const [selectedRequirementIds, setSelectedRequirementIds] = useState([]);
  const [selectedLegalKeywordIds, setSelectedLegalKeywordIds] = useState([]);
  const [requirementLabelMap, setRequirementLabelMap] = useState(new Map());
  const [isPrefillDone, setIsPrefillDone] = useState(false);
  const lastPrefillKeyRef = useRef("");
  const lastUpdatedRequirementsRef = useRef(null);

  const [currentTab, setCurrentTab] = useState(0);
  const [currentStepsSubTab, setCurrentStepsSubTab] = useState(0);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
  const [isTypeDisabled, setIsTypeDisabled] = useState(false);

  const handleLegalStandardChange = (value) => {
    setSelectedLegalStandardId(value);
    setSelectedChapter(null);
    setChapterOptionsList([]);
    setRequirementOptionsList([]);
  };

  const handleChapterChange = (value) => {
    setSelectedChapter(value);
    setRequirementOptionsList([]);
  };

  const isUpdateTestCaseValid = () => {
    return (
      testCaseId?.trim() !== "" &&
      !testCaseIdError &&
      testCaseName?.trim() !== "" &&
      !testCaseNameError &&
      description?.trim() !== "" &&
      !descriptionError &&
      status !== null &&
      status !== ""
    );
  };

  const isDetailsTabValid = () => {
    return (
      testCaseName?.trim() !== "" &&
      description?.trim() !== "" &&
      status !== null &&
      status !== ""
    );
  };

  const clearUpdateTestCaseFields = () => {
    setTestCaseId("");
    setTestCaseIdError("");
    setTestCaseName("");
    setTestCaseNameError("");
    setDescription("");
    setDescriptionError("");
    setEnvironment(null);
    setIsDestructible(false);
    setNeedNewVehicle(false);
    setEEComponentsOptionsList([]);
    setSelectedEEComponentIds([]);
    setSoftwareModulesOptionsList([]);
    setSelectedSoftwareModuleIds([]);
    setVehicleSystemsOptionsList([]);
    setSelectedVehicleSystemIds([]);
    setVehicleComponentsOptionsList([]);
    setSelectedVehicleComponentIds([]);
    setSelectedComponent(COMPONENT.EE);
    setType(null);
    setStatus(null);
    setPreConditions([{ ...EMPTY_PRE_CONDITION }]);
    setTestCaseSteps([{ ...EMPTY_STEPS }]);
    setExpectedResult("");
    setLegalStandardOptionsList([]);
    setLegalStandardsList([]);
    setSelectedLegalStandardId(null);
    setChapterOptionsList([]);
    setSelectedChapter(null);
    setRequirementOptionsList([]);
    setSelectedRequirementIds([]);
    setOriginalEEComponentIds([]);
    setOriginalRequirementIds([]);

    setSelectedLegalKeywordIds([]);
    setRequirementLabelMap(new Map());
    setCurrentTab(0);
    setIsTypeDisabled(false);
    setIsPrefillDone(false);
  };

  const handleUpdateTestCase = async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...testCasesActions.update_test_case,
          params: {
            test_case_id: mainTestCaseId,
            description: description,
            ...(selectedComponent === COMPONENT.EE && {
              component_keyword_to_link: {
                entity_type: "2",
                keyword_ids: selectedEEComponentIds?.map((eeId) =>
                  Number(eeId?.value),
                ),
              },
            }),
            ...(selectedComponent === COMPONENT.SWM && {
              component_keyword_to_link: {
                entity_type: "1",
                keyword_ids: selectedSoftwareModuleIds?.map((swmId) =>
                  Number(swmId?.value),
                ),
              },
            }),
            ...(selectedComponent === COMPONENT.VS && {
              component_keyword_to_link: {
                entity_type: "6",
                keyword_ids: selectedVehicleSystemIds?.map((vsId) =>
                  Number(vsId?.value),
                ),
              },
            }),
            ...(selectedComponent === COMPONENT.VC && {
              component_keyword_to_link: {
                entity_type: "3",
                keyword_ids: selectedVehicleComponentIds?.map((vsId) =>
                  Number(vsId?.value),
                ),
              },
            }),
            ...(type && { type }),
            ...(status && { status }),
            ...(preConditions?.length > 0 && {
              preconditions: preConditions
                .filter((pc) => pc?.description?.trim())
                .map((pc, idx) => ({
                  condition_number: idx + 1,
                  description: pc.description,
                })),
            }),
            ...(testCaseSteps?.length > 0 && {
              steps: testCaseSteps
                .filter(
                  (step) =>
                    step?.description?.trim() && step?.expected_result?.trim(),
                )
                .map((step, idx) => ({
                  step_number: idx + 1,
                  description: step.description,
                  expected_result: step.expected_result,
                })),
            }),
            ...(expectedResult && { expected_results: expectedResult }),
            ...(selectedRequirementIds?.length > 0 && {
              requirement_ids: selectedRequirementIds
                ?.map((rId) => Number(rId?.value))
                .filter((id) => !Number.isNaN(id) && id > 0),
            }),
            ...(selectedLegalKeywordIds?.length > 0 && {
              legal_keyword_to_link: {
                entity_type: "5",
                keyword_ids: selectedLegalKeywordIds?.map((kId) =>
                  Number(kId?.value || kId),
                ),
              },
            }),
          },
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      const updatedRequirementIds = (selectedRequirementIds || [])
        .map((req) => String(req?.value))
        .filter((id) => id && id !== "undefined" && id !== "null");
      lastUpdatedRequirementsRef.current = {
        testCaseId: Number(mainTestCaseId),
        requirementIds: updatedRequirementIds,
        requirementChips: (selectedRequirementIds || []).map((req) => ({
          value: req?.value,
          label: req?.label,
        })),
      };
      if (onSuccess) {
        onSuccess();
      }
      onClose(false);
      clearUpdateTestCaseFields();
      setSelectedTestCaseData(null);
    }
  };

  const handleAddPreConditionsFields = () => {
    setPreConditions((prev) => [...prev, { ...EMPTY_PRE_CONDITION }]);
  };

  const handlePreConditionsChange = (index, field, value) => {
    const updatedPreCondition = [...preConditions];
    updatedPreCondition[index] = {
      ...updatedPreCondition[index],
      [field]: value,
    };
    setPreConditions(updatedPreCondition);
  };

  const handleAddTestCaseStepsFields = () => {
    setTestCaseSteps((prev) => [...prev, { ...EMPTY_STEPS }]);
  };

  const handleTestCaseStepsChange = (index, field, value) => {
    const updatedSteps = [...testCaseSteps];
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value,
    };
    setTestCaseSteps(updatedSteps);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteAction?.type === "ee") {
      setSelectedEEComponentIds(
        selectedEEComponentIds.filter(
          (_, i) => i !== pendingDeleteAction.index,
        ),
      );
    } else if (pendingDeleteAction?.type === "requirement") {
      setSelectedRequirementIds(
        selectedRequirementIds.filter(
          (_, i) => i !== pendingDeleteAction.index,
        ),
      );
    }
    setOpenConfirmDialog(false);
    setPendingDeleteAction(null);
  };

  const getChipKey = (item, prefix) =>
    `${prefix}-${item?.value ?? item?.label}`;

  const handleEEChipClick = (eeComponent, index) => {
    const componentId = eeComponent?.value;
    const isOriginalData = originalEEComponentIds?.includes(componentId);

    if (mainTestCaseId && isOriginalData) {
      setPendingDeleteAction({ type: "ee", index });
      setOpenConfirmDialog(true);
      return;
    }

    setSelectedEEComponentIds(
      selectedEEComponentIds.filter((item) => item?.value !== componentId),
    );
  };

  const handleRequirementChipClick = (requirement, index) => {
    const requirementId = requirement?.value;
    const isOriginalData = originalRequirementIds?.includes(requirementId);

    if (mainTestCaseId && isOriginalData) {
      setPendingDeleteAction({ type: "requirement", index });
      setOpenConfirmDialog(true);
      return;
    }

    setSelectedRequirementIds(
      selectedRequirementIds.filter((item) => item?.value !== requirementId),
    );
  };

  const getDynamicItemKey = (item, prefix) =>
    `${prefix}-${item?.value ?? ""}-${item?.label ?? item?.description ?? item?.expected_result ?? ""}`;

  const handleSoftwareModuleChipRemove = (softwareModule) => {
    setSelectedSoftwareModuleIds(
      selectedSoftwareModuleIds.filter(
        (item) => item?.value !== softwareModule?.value,
      ),
    );
  };

  const handleLegalKeywordChipRemove = (legalKeyword) => {
    setSelectedLegalKeywordIds(
      selectedLegalKeywordIds.filter(
        (item) => item?.value !== legalKeyword?.value,
      ),
    );
  };

  const handleVehicleSystemChipRemove = (vehicleSystem) => {
    setSelectedVehicleSystemIds(
      selectedVehicleSystemIds.filter(
        (item) => item?.value !== vehicleSystem?.value,
      ),
    );
  };

  const handleVehicleComponentChipRemove = (vehicleComponent) => {
    setSelectedVehicleComponentIds(
      selectedVehicleComponentIds.filter(
        (item) => item?.value !== vehicleComponent?.value,
      ),
    );
  };

  const handleRemovePreCondition = (index) => {
    const updated = preConditions.filter((_, i) => i !== index);
    setPreConditions(updated.length ? updated : [{ ...EMPTY_PRE_CONDITION }]);
  };

  const handleRemoveTestCaseStep = (index) => {
    const updated = testCaseSteps.filter((_, i) => i !== index);
    setTestCaseSteps(updated.length ? updated : [{ ...EMPTY_STEPS }]);
  };

  useEffect(() => {
    const handleGetEEComponentsGlobally = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_ee_components_globally,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        ),
      );
      if (resp.error) {
        setEEComponentsOptionsList([]);
      } else {
        const filteredEEComponentsOptionsList = resp?.data
          ?.filter((ee) => ee?.status !== false)
          ?.map((ee) => mapEntityToOption(ee, COMPONENT.EE));
        setEEComponentsOptionsList(filteredEEComponentsOptionsList);
      }
    };

    if (open) {
      handleGetEEComponentsGlobally();
    }
  }, [open, dispatch]);

  useEffect(() => {
    const handleGetTestEnvironments = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...adminSettingsAPI.get_environment,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        ),
      );

      const options = Array.isArray(resp?.data)
        ? resp.data.map((env) => ({
            label: env?.name,
            value: env?.id,
          }))
        : [];
      setEnvironmentOptionsList(options);
    };

    if (open) {
      handleGetTestEnvironments();
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (environment === null || !environmentOptionsList.length) return;

    const selectedOption = environmentOptionsList.find(
      (option) =>
        String(option?.value) === String(environment) ||
        option?.label === environment,
    );

    if (selectedOption && selectedOption.value !== environment) {
      setEnvironment(selectedOption.value);
    }
  }, [environment, environmentOptionsList]);

  useEffect(() => {
    const handleGetSoftwareModules = async ({
      projectId,
      variantId,
      eeComponentId,
    }) => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_swm_globally,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        ),
      );
      if (resp.error) {
        setSoftwareModulesOptionsList([]);
        setSelectedSoftwareModuleIds([]);
      } else {
        const filteredSoftwareModulesOptionsList = resp?.data
          ?.filter((swm) => swm?.status !== false)
          ?.map((swm) => mapEntityToOption(swm, COMPONENT.SWM));
        setSoftwareModulesOptionsList(filteredSoftwareModulesOptionsList);
      }
    };
    if (open) {
      handleGetSoftwareModules({});
    }
  }, [open, dispatch]);

  useEffect(() => {
    const handleGetVehicleSystems = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_vs_globally,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        ),
      );
      if (resp.error) {
        setVehicleSystemsOptionsList([]);
        setSelectedVehicleSystemIds([]);
      } else {
        const filteredVehicleSystemsOptionsList = resp?.data
          ?.filter((vs) => vs?.status !== false)
          ?.map((vs) => mapEntityToOption(vs, COMPONENT.VS));
        setVehicleSystemsOptionsList(filteredVehicleSystemsOptionsList);
      }
    };

    if (open) {
      handleGetVehicleSystems();
    }
  }, [open, dispatch]);

  useEffect(() => {
    const handleGetVehicleComponents = async ({
      projectId,
      variantId,
      vehicleSystemId,
    }) => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_vc_globally,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        ),
      );
      if (resp.error) {
        setVehicleComponentsOptionsList([]);
        setSelectedVehicleComponentIds([]);
      } else {
        const filteredVehicleComponentsOptionsList = resp?.data
          ?.filter((vc) => vc?.status !== false)
          ?.map((vc) => mapEntityToOption(vc, COMPONENT.VC));
        setVehicleComponentsOptionsList(filteredVehicleComponentsOptionsList);
      }
    };

    if (open) {
      handleGetVehicleComponents({});
    }
  }, [open, dispatch]);

  useEffect(() => {
    const handleGetLegalStandards = async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_legal_standards,
            params: {
              page_number: 1,
              page_size: 10000,
            },
          }),
        ),
      );
      if (resp.error) {
        setLegalStandardsList([]);
        setLegalStandardOptionsList([]);
        setSelectedLegalStandardId(null);
      } else {
        setLegalStandardsList(resp?.data);
        const filteredLegalStandardOptionsList = resp?.data?.map((ls) => ({
          value: ls?.ls_id,
          label: ls?.standard_number,
        }));
        setLegalStandardOptionsList(filteredLegalStandardOptionsList);
      }
    };

    if (open) {
      handleGetLegalStandards();
    }
  }, [open, dispatch]);

  useEffect(() => {
    const filteredSelectedLegalStandardDetails = legalStandardsList?.find(
      (ls) => ls?.ls_id === selectedLegalStandardId,
    );
    const filteredChaptersOptionsList =
      filteredSelectedLegalStandardDetails?.chapters?.map((ch) => ({
        value: ch,
        label: ch,
      }));
    setChapterOptionsList(filteredChaptersOptionsList);
  }, [legalStandardsList, selectedLegalStandardId]);

  useEffect(() => {
    const handleGetLegalRequirements = async ({ legalStandardId, chapter }) => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...testCasesActions.get_legal_requirements,
            params: {
              ls_id: legalStandardId,
              chapter: chapter,
              sort_column: "created_at",
              sort_order: "ASC",
              page_number: 1,
              page_size: 500,
            },
          }),
        ),
      );
      if (resp.error) {
        setRequirementOptionsList([]);
      } else {
        const filteredRequirementOptionsList = resp?.data?.map((lr) => ({
          value: lr?.lr_id,
          label: lr?.req_number,
        }));
        setRequirementOptionsList(filteredRequirementOptionsList);
      }
    };

    if (open && selectedLegalStandardId && selectedChapter) {
      handleGetLegalRequirements({
        legalStandardId: selectedLegalStandardId,
        chapter: selectedChapter,
      });
    }
  }, [open, selectedLegalStandardId, selectedChapter, dispatch]);

  useEffect(() => {
    const formatStatusLabel = (status) => {
      return status
        ?.split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const filteredTestCaseStatus = testCaseStatus?.map((tcs) => ({
      value: tcs,
      label: formatStatusLabel(tcs),
    }));
    setStatusOptionsList(filteredTestCaseStatus);
  }, [testCaseStatus, open]);

  const applyComponentLinkPrefill = useCallback(
    (componentLink) => {
      if (!componentLink) {
        setIsTypeDisabled(false);
        return;
      }

      const { entity_type, entity_ids = [] } = componentLink;
      setSelectedComponent(entity_type);
      setIsTypeDisabled(Array.isArray(entity_ids) && entity_ids.length > 0);

      if (entity_type === COMPONENT.EE) {
        setSelectedEEComponentIds(
          mapEntityIdsToOptions(entity_ids, eeComponentsOptionsList),
        );
        setOriginalEEComponentIds(entity_ids);
        return;
      }
      if (entity_type === COMPONENT.SWM) {
        setSelectedSoftwareModuleIds(
          mapEntityIdsToOptions(entity_ids, softwareModulesOptionsList),
        );
        return;
      }
      if (entity_type === COMPONENT.VS) {
        setSelectedVehicleSystemIds(
          mapEntityIdsToOptions(entity_ids, vehicleSystemsOptionsList),
        );
        return;
      }
      if (entity_type === COMPONENT.VC) {
        setSelectedVehicleComponentIds(
          mapEntityIdsToOptions(entity_ids, vehicleComponentsOptionsList),
        );
      }
    },
    [
      eeComponentsOptionsList,
      softwareModulesOptionsList,
      vehicleSystemsOptionsList,
      vehicleComponentsOptionsList,
    ],
  );

  const applyRequirementsPrefill = useCallback(
    (testCaseData, requirements = []) => {
      if (!requirements?.length) return;

      const { chapter, ls_id: lsId } = requirements[0] || {};
      const allRequirementIds = requirements.flatMap(
        (req) => req?.requirement_ids || [],
      );
      const uniqueRequirementIds = Array.from(
        new Set(allRequirementIds.map(String)),
      );

      const cached = lastUpdatedRequirementsRef.current;
      const shouldUseCache =
        cached?.testCaseId === Number(testCaseData?.test_case_id) &&
        Array.isArray(cached?.requirementIds) &&
        cached.requirementIds.some(
          (id) => !uniqueRequirementIds.includes(String(id)),
        );

      setSelectedLegalStandardId(lsId);
      setSelectedChapter(chapter);

      if (shouldUseCache) {
        setOriginalRequirementIds(cached.requirementIds);
        setSelectedRequirementIds(cached.requirementChips || []);
        return;
      }
      setOriginalRequirementIds(uniqueRequirementIds);
    },
    [],
  );

  useEffect(() => {
    if (!open || !selectedTestCaseData || isPrefillDone) return;

    const prefillKey = `${selectedTestCaseData?.test_case_id || ""}-${
      selectedTestCaseData?.updated_at || ""
    }`;

    if (prefillKey && lastPrefillKeyRef.current !== prefillKey) {
      lastPrefillKeyRef.current = prefillKey;
      setIsPrefillDone(false);
      setSelectedRequirementIds([]);
      setOriginalRequirementIds([]);
      setSelectedLegalKeywordIds([]);

      setRequirementLabelMap(new Map());
    }

    setMainTestCaseId(Number(selectedTestCaseData?.test_case_id));
    setTestCaseId(selectedTestCaseData?.manual_test_case_id);
    setTestCaseName(selectedTestCaseData?.name);
    setDescription(selectedTestCaseData?.description);
    const savedEnvironment =
      selectedTestCaseData?.test_environment_id ??
      selectedTestCaseData?.test_environment ??
      selectedTestCaseData?.environment;
    setEnvironment(
      typeof savedEnvironment === "object"
        ? (savedEnvironment?.id ??
            savedEnvironment?.environment_id ??
            savedEnvironment?.value ??
            savedEnvironment?.name ??
            null)
        : (savedEnvironment ?? null),
    );
    setIsDestructible(
      toBoolean(
        selectedTestCaseData?.is_destructable ??
          selectedTestCaseData?.is_destructible ??
          selectedTestCaseData?.destructable,
      ),
    );
    setNeedNewVehicle(toBoolean(selectedTestCaseData?.need_new_vehicle));

    applyComponentLinkPrefill(selectedTestCaseData?.component_to_link?.[0]);

    if (selectedTestCaseData?.keywords) {
      // Prefill legal keywords (type 5)
      const legalKeywords = selectedTestCaseData.keywords.filter(
        (kw) => kw?.entity_type === "5" || kw?.entity_type === 5,
      );
      if (legalKeywords.length > 0) {

        const mappedKeywords = legalKeywords.map((kw) => ({
          value: kw.keyword_id,
          label: kw.keyword_name,
        }));

        setSelectedLegalKeywordIds(mappedKeywords);
      }

      // Prefill EE Keywords (type 2)
      const eeKeywords = selectedTestCaseData.keywords.filter(
        (kw) => kw?.entity_type === "2" || kw?.entity_type === 2,
      );
      if (eeKeywords.length > 0) {
        setSelectedComponent(COMPONENT.EE);
        setIsTypeDisabled(true);
        const mappedEE = eeKeywords.map((kw) => ({
          value: kw.keyword_id,
          label: kw.keyword_name,
        }));
        setSelectedEEComponentIds(mappedEE);
        setOriginalEEComponentIds(mappedEE.map((k) => k.value));
      }

      // Prefill SWM Keywords (type 1)
      const swmKeywords = selectedTestCaseData.keywords.filter(
        (kw) => kw?.entity_type === "1" || kw?.entity_type === 1,
      );
      if (swmKeywords.length > 0) {
        setSelectedComponent(COMPONENT.SWM);
        setIsTypeDisabled(true);
        const mappedSWM = swmKeywords.map((kw) => ({
          value: kw.keyword_id,
          label: kw.keyword_name,
        }));
        setSelectedSoftwareModuleIds(mappedSWM);
      }

      // Prefill System Keywords (VS, type 6)
      const vsKeywords = selectedTestCaseData.keywords.filter(
        (kw) => kw?.entity_type === "6" || kw?.entity_type === 6,
      );
      if (vsKeywords.length > 0) {
        setSelectedComponent(COMPONENT.VS);
        setIsTypeDisabled(true);
        const mappedVS = vsKeywords.map((kw) => ({
          value: kw.keyword_id,
          label: kw.keyword_name,
        }));
        setSelectedVehicleSystemIds(mappedVS);
      }

      // Prefill VC Keywords (type 3)
      const vcKeywords = selectedTestCaseData.keywords.filter(
        (kw) => kw?.entity_type === "3" || kw?.entity_type === 3,
      );
      if (vcKeywords.length > 0) {
        setSelectedComponent(COMPONENT.VC);
        setIsTypeDisabled(true);
        const mappedVC = vcKeywords.map((kw) => ({
          value: kw.keyword_id,
          label: kw.keyword_name,
        }));
        setSelectedVehicleComponentIds(mappedVC);
      }
    }

    setType(selectedTestCaseData?.type);
    setStatus(selectedTestCaseData?.status);
    setPreConditions(
      selectedTestCaseData?.preconditions || [{ ...EMPTY_PRE_CONDITION }],
    );
    setTestCaseSteps(selectedTestCaseData?.steps || [{ ...EMPTY_STEPS }]);
    setExpectedResult(selectedTestCaseData?.expected_results || "");

    applyRequirementsPrefill(
      selectedTestCaseData,
      selectedTestCaseData?.requirements || [],
    );

    setIsPrefillDone(true);
  }, [
    open,
    selectedTestCaseData,
    eeComponentsOptionsList,
    softwareModulesOptionsList,
    vehicleSystemsOptionsList,
    vehicleComponentsOptionsList,
    isPrefillDone,
    applyComponentLinkPrefill,
    applyRequirementsPrefill,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!selectedTestCaseData?.requirements?.length) return;
    let isActive = true;

    (async () => {
      const requestItems = getUniqueLegalRequirementRequests(
        selectedTestCaseData.requirements || [],
      );
      const responses = await Promise.all(
        requestItems.map((item) =>
          Promise.resolve(dispatch(
            HandleApiActions({
              ...testCasesActions.get_legal_requirements,
              params: {
                ls_id: item.lsId,
                chapter: item.chapter,
                sort_column: "created_at",
                sort_order: "ASC",
                page_number: 1,
                page_size: 500,
              },
            }),
          )),
        ),
      );

      if (!isActive) return;
      setRequirementLabelMap(buildRequirementLabelMap(responses));
    })();

    return () => {
      isActive = false;
    };
  }, [open, selectedTestCaseData, dispatch]);

  useEffect(() => {
    if (!open) return;
    if (!isPrefillDone) return;
    if (selectedRequirementIds?.length) return;
    if (!originalRequirementIds?.length) return;

    const mappedReqIds = originalRequirementIds?.map((id) => {
      const labelFromMap = requirementLabelMap.get(String(id));
      const found = requirementOptionsList?.find(
        (req) => String(req?.value) === String(id),
      );
      return {
        value: id,
        label: labelFromMap || found?.label || `Requirement ${id}`,
      };
    });
    setSelectedRequirementIds(mappedReqIds || []);
  }, [
    open,
    isPrefillDone,
    originalRequirementIds,
    requirementOptionsList,
    requirementLabelMap,
    selectedRequirementIds?.length,
  ]);

  useEffect(() => {
    if (!open) return;
    if (!requirementLabelMap.size) return;
    if (!selectedRequirementIds?.length) return;

    const updated = selectedRequirementIds.map((req) => {
      const labelFromMap = requirementLabelMap.get(String(req?.value));
      if (!labelFromMap || labelFromMap === req?.label) return req;
      return { ...req, label: labelFromMap };
    });

    const changed = updated.some(
      (req, idx) => req?.label !== selectedRequirementIds[idx]?.label,
    );
    if (changed) {
      setSelectedRequirementIds(updated);
    }
  }, [open, requirementLabelMap, selectedRequirementIds]);

  const renderDetailsTab = () => (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: "1000px" }}>
      <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
        <Box sx={{ flex: 1 }}>
          <CustomFormTextFieldV2
            label="Test Case ID"
            required={true}
            value={testCaseId}
            setValue={setTestCaseId}
            disabled={true}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                height: 32,
                borderRadius: 0,
                "&:hover fieldset": {
                  borderColor: "#000000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <CustomSelectV2
            required={true}
            size={"small"}
            label={"Status"}
            variant={"outlined"}
            optionsList={statusOptionsList}
            selectedValue={status}
            handleOnChange={(e) => setStatus(e.target.value)}
          />
        </Box>
      </Box>

      <CustomFormTextFieldV2
        label="Test Case Name"
        required={true}
        value={testCaseName}
        setValue={setTestCaseName}
        disabled={true}
        txtFldStyle={{
          width: "100%",
          "& .MuiOutlinedInput-root": { height: 32 },
          "& .MuiOutlinedInput-root fieldset": { borderRadius: 0 },
          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
        }}
      />

      <Box>
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            ...componentStyle.font.form.textFieldLabel,
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          Description <span style={{ color: "red" }}>*</span>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              "&:hover fieldset": {
                borderColor: "#000000",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#000000",
              },
            },
          }}
        />
      </Box>

      <CustomSelectV2
        label="Environment"
        selectedValue={environment}
        optionsList={environmentOptionsList}
        required={true}
        disabled={true}
      />

      <CustomRadioButtonV2
        value={String(isDestructible)}
        data={[
          { value: "true", label: "Yes" },
          { value: "false", label: "No" },
        ]}
        displayDirection="row"
        label="Destructible"
        required={true}
        disabled={true}
      />

      {isDestructible && (
        <CustomCheckBoxV2
          value={needNewVehicle}
          label="Need New Vehicle"
          disabled={true}
        />
      )}
    </Stack>
  );

  const renderLinkToTab = () => (
    <Stack spacing={3} sx={{ width: "100%", maxWidth: "1000px", p: 2 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          Type
        </Typography>
        <Box sx={{ ml: "10px" }}>
          <CustomRadioButtonV2
            value={selectedComponent}
            onChange={(e) => setSelectedComponent(e.target.value)}
            data={[
              { value: COMPONENT.VS, label: "Vehicle System" },
              { value: COMPONENT.VC, label: "Vehicle Component" },
              { value: COMPONENT.EE, label: "EE Component" },
              { value: COMPONENT.SWM, label: "Software Module" },
            ]}
            displayDirection="row"
            disabled={isTypeDisabled}
          />
        </Box>
      </Box>

      {selectedComponent === COMPONENT.EE && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select Component Keyword / Tags
            </Typography>
          </Box>

          <CustomMultiSelectAutocompleteWithApiV2
            apiAction={adminSettingsAPI.get_keywords}
            params={{ page_number: 1, page_size: 10000, type: 2 }}
            searchKey={"name"}
            mapResponseToOptions={(data) =>
              (data || [])
                .filter((keyword) => keyword?.status !== false)
                .map((keyword) => ({
                  value: keyword?.id,
                  label: keyword?.name,
                }))
            }
            selectedValues={selectedEEComponentIds}
            setSelectedValues={setSelectedEEComponentIds}
            size="small"
            variant="outlined"
            placeholder="Search Component Keyword"
            paperSx={{}}
            skipInitialSearch={false}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": { borderColor: "#000000" },
              },
            }}
            enableSelectSimilar={true}
            enableSelectAll={true}
          />

          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedEEComponentIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedEEComponentIds?.map((eeComponent, index) => (
              <Box
                key={getChipKey(eeComponent, "ee")}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() => handleEEChipClick(eeComponent, index)}
              >
                {eeComponent?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {selectedComponent === COMPONENT.SWM && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select Module Keyword / Tags
            </Typography>
          </Box>
          <CustomMultiSelectAutocompleteWithApiV2
            apiAction={adminSettingsAPI.get_keywords}
            params={{ page_number: 1, page_size: 10000, type: 1 }}
            searchKey={"name"}
            mapResponseToOptions={(data) =>
              (data || [])
                .filter((keyword) => keyword?.status !== false)
                .map((keyword) => ({
                  value: keyword?.id,
                  label: keyword?.name,
                }))
            }
            selectedValues={selectedSoftwareModuleIds}
            setSelectedValues={setSelectedSoftwareModuleIds}
            size="small"
            variant="outlined"
            placeholder="Search Module Keyword"
            paperSx={{}}
            skipInitialSearch={false}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": { borderColor: "#000000" },
              },
            }}
            enableSelectSimilar={true}
            enableSelectAll={true}
          />
          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedSoftwareModuleIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedSoftwareModuleIds?.map((softwareModule) => (
              <Box
                key={getChipKey(softwareModule, "swm")}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() => handleSoftwareModuleChipRemove(softwareModule)}
              >
                {softwareModule?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {selectedComponent === COMPONENT.VS && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select System Keyword / Tags
            </Typography>
          </Box>
          <CustomMultiSelectAutocompleteWithApiV2
            apiAction={adminSettingsAPI.get_keywords}
            params={{ page_number: 1, page_size: 10000, type: 6 }}
            searchKey={"name"}
            mapResponseToOptions={(data) =>
              (data || [])
                .filter((keyword) => keyword?.status !== false)
                .map((keyword) => ({
                  value: keyword?.id,
                  label: keyword?.name,
                }))
            }
            selectedValues={selectedVehicleSystemIds}
            setSelectedValues={setSelectedVehicleSystemIds}
            size="small"
            variant="outlined"
            placeholder="Search System Keyword"
            paperSx={{}}
            skipInitialSearch={false}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": { borderColor: "#000000" },
              },
            }}
            enableSelectSimilar={true}
            enableSelectAll={true}
          />
          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedVehicleSystemIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedVehicleSystemIds?.map((vehicleSystem) => (
              <Box
                key={getChipKey(vehicleSystem, "vs")}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() => handleVehicleSystemChipRemove(vehicleSystem)}
              >
                {vehicleSystem?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {selectedComponent === COMPONENT.VC && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#666" }}>
              Search / Select Component Keyword / Tags
            </Typography>
          </Box>
          <CustomMultiSelectAutocompleteWithApiV2
            apiAction={adminSettingsAPI.get_keywords}
            params={{ page_number: 1, page_size: 10000, type: 3 }}
            searchKey={"name"}
            mapResponseToOptions={(data) =>
              (data || [])
                .filter((keyword) => keyword?.status !== false)
                .map((keyword) => ({
                  value: keyword?.id,
                  label: keyword?.name,
                }))
            }
            selectedValues={selectedVehicleComponentIds}
            setSelectedValues={setSelectedVehicleComponentIds}
            size="small"
            variant="outlined"
            placeholder="Search Component Keyword"
            paperSx={{}}
            skipInitialSearch={false}
            txtFldStyle={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "&:hover fieldset": { borderColor: "#000000" },
                "&.Mui-focused fieldset": { borderColor: "#000000" },
              },
            }}
            enableSelectSimilar={true}
            enableSelectAll={true}
          />
          <Box
            sx={{
              maxHeight: "95px",
              width: "100%",
              overflow: "auto",
              px: 0.5,
              pt: 0.5,
              mb: 2,
              ...(selectedVehicleComponentIds?.length > 0 && {
                border: "1px solid lightgrey",
              }),
            }}
          >
            {selectedVehicleComponentIds?.map((vehicleComponent) => (
              <Box
                key={getChipKey(vehicleComponent, "vc")}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  mb: 0.5,
                  mr: 0.5,
                  backgroundColor: "#232323",
                  color: "white",
                  borderRadius: 0,
                  padding: "4px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                }}
                onClick={() =>
                  handleVehicleComponentChipRemove(vehicleComponent)
                }
              >
                {vehicleComponent?.label}
                <span
                  style={{
                    marginLeft: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ×
                </span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <CustomSelectV2
        size={"small"}
        label={"Select Legal Standard No"}
        optionsList={legalStandardOptionsList}
        selectedValue={selectedLegalStandardId}
        handleOnChange={(e) => handleLegalStandardChange(e.target.value)}
        required={false}
        placeholder={false}
      />
      <CustomSelectV2
        size={"small"}
        label={"Select Chapter"}
        optionsList={chapterOptionsList}
        selectedValue={selectedChapter}
        handleOnChange={(e) => handleChapterChange(e.target.value)}
        required={false}
        placeholder={false}
      />
      <Box>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            Search / Select Requirement No
          </Typography>
        </Box>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={requirementOptionsList || []}
          value={selectedRequirementIds || []}
          onChange={(event, newValue) => {
            setSelectedRequirementIds(newValue);
          }}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          renderOption={(props, option, { selected }) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                p: "8px 12px !important",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <Checkbox
                checked={selected}
                sx={{
                  marginRight: 1,
                  color: "#000",
                  "&.Mui-checked": {
                    color: "#000",
                  },
                }}
              />
              {option?.label}
            </Box>
          )}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  borderRadius: 0,
                  "&:hover fieldset": {
                    borderColor: "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                  },
                },
              }}
            />
          )}
          slotProps={{
            paper: {
              sx: {
                maxHeight: 250,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#555",
                  },
                },
              },
            },
          }}
        />

        <Box
          sx={{
            maxHeight: "95px",
            width: "100%",
            overflow: "auto",
            px: 0.5,
            pt: 0.5,
            mb: 2,
            ...(selectedRequirementIds?.length > 0 && {
              border: "1px solid lightgrey",
            }),
          }}
        >
          {selectedRequirementIds?.map((requirement, index) => (
            <Box
              key={getChipKey(requirement, "req")}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mb: 0.5,
                mr: 0.5,
                backgroundColor: "#232323",
                color: "white",
                borderRadius: 0,
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#1a1a1a",
                },
              }}
              onClick={() => handleRequirementChipClick(requirement, index)}
            >
              {requirement?.label}
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ×
              </span>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            Search / Select Legal Keyword / Tags
          </Typography>
        </Box>
        <CustomMultiSelectAutocompleteWithApiV2
          apiAction={adminSettingsAPI.get_keywords}
          params={{ page_number: 1, page_size: 10000, type: 5 }}
          searchKey={"name"}
          mapResponseToOptions={(data) =>
            (data || [])
              .filter((keyword) => keyword?.status !== false)
              .map((keyword) => ({
                value: keyword?.id,
                label: keyword?.name,
              }))
          }
          selectedValues={selectedLegalKeywordIds}
          setSelectedValues={setSelectedLegalKeywordIds}
          size="small"
          variant="outlined"
          placeholder="Search Legal Keyword"
          paperSx={{}}
          skipInitialSearch={false}
          txtFldStyle={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              "&:hover fieldset": { borderColor: "#000000" },
              "&.Mui-focused fieldset": { borderColor: "#000000" },
            },
          }}
          enableSelectSimilar={true}
          enableSelectAll={true}
        />

        <Box
          sx={{
            maxHeight: "95px",
            width: "100%",
            overflow: "auto",
            px: 0.5,
            pt: 0.5,
            mb: 2,
            ...(selectedLegalKeywordIds?.length > 0 && {
              border: "1px solid lightgrey",
            }),
          }}
        >
          {selectedLegalKeywordIds?.map((legalKeyword, index) => (
            <Box
              key={getChipKey(legalKeyword, "lkw")}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                mb: 0.5,
                mr: 0.5,
                backgroundColor: "#232323",
                color: "white",
                borderRadius: 0,
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#1a1a1a",
                },
              }}
              onClick={() => handleLegalKeywordChipRemove(legalKeyword)}
            >
              {legalKeyword?.label}
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ×
              </span>
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );

  const renderStepsTab = () => (
    <Stack spacing={0} sx={{ width: "100%", maxWidth: "1000px" }}>
      <CustomTabsV2
        value={currentStepsSubTab}
        disableTabClick={false}
        centerTabs={false}
        onTabChange={(newTab) => setCurrentStepsSubTab(newTab)}
        tabs={[
          {
            name: "Pre Condition",
            count: preConditions?.length || 0,
            content: renderPreConditionsTab(),
          },
          {
            name: "Steps",
            count: testCaseSteps?.length || 0,
            content: renderStepsContentTab(),
          },
          {
            name: "Expected Result",
            count: "",
            content: renderExpectedResultTab(),
          },
        ]}
      />
    </Stack>
  );

  const renderPreConditionsTab = () => (
    <Box>
      {preConditions?.map((item, index) => (
        <Box
          key={getDynamicItemKey(item, "pre")}
          sx={{
            bgcolor: "#f9f9f9",
            borderRadius: "4px",
            position: "relative",
            p: 2,
            mb: 1.5,
          }}
        >
          <IconButton
            size="small"
            sx={{ position: "absolute", right: 2, top: 2 }}
            onClick={() => handleRemovePreCondition(index)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ fontSize: "14px" }}>
            {index + 1}.Pre Condition
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={item?.description}
            onChange={(e) =>
              handlePreConditionsChange(index, "description", e.target.value)
            }
            variant="outlined"
            size="small"
            sx={{
              mt: 0.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                "&:hover fieldset": {
                  borderColor: "#000000",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#000000",
                },
              },
            }}
          />
        </Box>
      ))}
      <Box>
        <IconButton
          size="small"
          onClick={handleAddPreConditionsFields}
          sx={{ mt: 0.5 }}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Box>
  );

  const renderStepsContentTab = () => (
    <Box>
      {testCaseSteps?.map((item, index) => (
        <Box
          key={getDynamicItemKey(item, "step")}
          sx={{
            bgcolor: "#f9f9f9",
            borderRadius: "4px",
            position: "relative",
            p: 2,
            mb: 1.5,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            sx={{ position: "absolute", right: 2, top: 2 }}
            onClick={() => handleRemoveTestCaseStep(index)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="caption" sx={{ fontSize: "14px" }}>
              {index + 1}.Step
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={item?.description}
              onChange={(e) =>
                handleTestCaseStepsChange(index, "description", e.target.value)
              }
              variant="outlined"
              size="small"
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "&:hover fieldset": {
                    borderColor: "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                  },
                },
              }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: "14px" }}>
              {index + 1}.Expected Result
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={item?.expected_result}
              onChange={(e) =>
                handleTestCaseStepsChange(
                  index,
                  "expected_result",
                  e.target.value,
                )
              }
              variant="outlined"
              size="small"
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "&:hover fieldset": {
                    borderColor: "#000000",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                  },
                },
              }}
            />
          </Box>
        </Box>
      ))}
      <IconButton
        size="small"
        onClick={handleAddTestCaseStepsFields}
        sx={{ mt: 0.5 }}
      >
        <AddIcon />
      </IconButton>
    </Box>
  );

  const renderExpectedResultTab = () => (
    <Box>
      <Typography sx={{ mb: 1 }}>Expected Result</Typography>
      <TextField
        fullWidth
        multiline
        rows={3}
        value={expectedResult}
        onChange={(e) => setExpectedResult(e.target.value)}
        variant="outlined"
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
            "&:hover fieldset": {
              borderColor: "#000000",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#000000",
            },
          },
        }}
      />
    </Box>
  );

  const dialogActionButtonStyle = currentTab === 0 ? "continuous" : "spaced";
  const dialogSuccessButtonName = currentTab === 2 ? "Update" : "Save & Next";
  const dialogRejectButtonName = currentTab === 0 ? "Cancel" : "Back";
  let isDialogActionDisabled = false;
  if (currentTab === 0) {
    isDialogActionDisabled = !isDetailsTabValid();
  } else if (currentTab === 2) {
    isDialogActionDisabled = !isUpdateTestCaseValid();
  }

  return (
    <>
      <CustomDialogBoxV2
        open={open}
        onClose={() => {
          onClose(false);
          clearUpdateTestCaseFields();
          setSelectedTestCaseData(null);
        }}
        title="Update Test Case"
        actionButtonStyle={dialogActionButtonStyle}
        successButtonName={dialogSuccessButtonName}
        rejectButtonName={dialogRejectButtonName}
        onClickSuccess={() => {
          if (currentTab === 2) {
            handleUpdateTestCase();
          } else {
            setCurrentTab(currentTab + 1);
          }
        }}
        onClickReject={() => {
          if (currentTab === 0) {
            onClose(false);
            clearUpdateTestCaseFields();
            setSelectedTestCaseData(null);
            setCurrentTab(0);
          } else {
            setCurrentTab(currentTab - 1);
          }
        }}
        onDisabled={isDialogActionDisabled}
        paperSx={{ width: "1000px", maxWidth: "95vw" }}
        contentSx={{ p: 0 }}
      >
        <CustomTabsV2
          value={currentTab}
          disableTabClick={true}
          centerTabs={true}
          onTabChange={(newTab) => setCurrentTab(newTab)}
          tabs={[
            {
              name: "Details",
              count: "",
              content: renderDetailsTab(),
            },
            {
              name: "Link To",
              count: "",
              content: renderLinkToTab(),
            },
            {
              name: "Steps",
              count: "",
              content: renderStepsTab(),
            },
          ]}
        />
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setPendingDeleteAction(null);
        }}
        title={"Confirm Delete"}

        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleConfirmDelete}
        onClickReject={() => {
          setOpenConfirmDialog(false);
          setPendingDeleteAction(null);
        }}
        actionButtonStyle={"continuous"}
        contentSx={{ width: 450 }}
      >
        <DialogContentText>
          {" "}
          This change will affect if this test case is added in a campaign. Are
          you sure you want to delete this item?
        </DialogContentText>
      </CustomDialogBoxV2>
    </>
  );
}

UpdateTestCaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedTestCaseData: PropTypes.shape({
    test_case_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    manual_test_case_id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    test_environment_id: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    test_environment: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.object,
    ]),
    environment: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.object,
    ]),
    is_destructable: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
      PropTypes.string,
    ]),
    is_destructible: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
      PropTypes.string,
    ]),
    destructable: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
      PropTypes.string,
    ]),
    need_new_vehicle: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.number,
      PropTypes.string,
    ]),
    status: PropTypes.string,
    type: PropTypes.string,
    updated_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expected_results: PropTypes.string,
    preconditions: PropTypes.arrayOf(
      PropTypes.shape({
        condition_number: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
        description: PropTypes.string,
      }),
    ),
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        step_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        description: PropTypes.string,
        expected_result: PropTypes.string,
      }),
    ),
    component_to_link: PropTypes.arrayOf(
      PropTypes.shape({
        entity_type: PropTypes.string,
        entity_ids: PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        ),
      }),
    ),
    requirements: PropTypes.arrayOf(
      PropTypes.shape({
        ls_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        chapter: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        requirement_ids: PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        ),
      }),
    ),
    keywords: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        entity_type: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  setSelectedTestCaseData: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default UpdateTestCaseDialog;
